"use client";

import { logger } from "@utilities/logger";
import { useCallback, useEffect, useRef, useMemo } from "react";
import {
  useMotionValue,
  animate,
  cancelFrame,
  frame,
  type MotionValue,
  mix,
  useInView,
  usePageInView,
} from "motion/react";
import { useDebounce } from "use-debounce";

const MAX_SLIDES = 8;

interface Uniforms {
  intensity: (WebGLUniformLocation | null)[];
  depthInfluence: (WebGLUniformLocation | null)[];
  focusPoint: (WebGLUniformLocation | null)[];
  resolution: WebGLUniformLocation | null;
  images: WebGLUniformLocation | null;
  depthMaps: WebGLUniformLocation | null;
  imageSize: WebGLUniformLocation | null;
  opacity: WebGLUniformLocation | null;
  selectedIndex: WebGLUniformLocation | null;
  targetIndex: WebGLUniformLocation | null;
  blendFactor: WebGLUniformLocation | null;
}

type Range = [from: number, to: number];
type AnimationParam = number | Range | [Range, Range];

export interface DollyZoomSlide {
  imageUrl: string;
  depthMapUrl: string;
  placeholder: string;
  intensity: AnimationParam;
  depthInfluence: AnimationParam;
  focusPoint: AnimationParam;
}

function isNormalizedParam(value: AnimationParam): value is [Range, Range] {
  return Array.isArray(value) && Array.isArray(value[0]);
}

function normalizeParam(value: AnimationParam): [Range, Range] {
  if (isNormalizedParam(value)) {
    return value;
  }
  if (typeof value === "number") {
    return [
      [value, value],
      [value, value],
    ];
  }
  return [value, value];
}

function DollyZoom({
  idleProgress,
  transitionProgress,
  opacity = 1,
  onRender,
  slides,
  selectedIndex = 0,
}: {
  idleProgress: MotionValue<number>;
  transitionProgress: MotionValue<number>;
  opacity?: number | MotionValue<number>;
  onRender?: () => void;
  slides: DollyZoomSlide[];
  selectedIndex?: number;
}) {
  if (slides.length > MAX_SLIDES) {
    throw new Error(`Amount of slides must not be greater than ${MAX_SLIDES}.`);
  }

  const imageUrls = useMemo(() => slides.map(slide => slide.imageUrl), [slides]);
  const depthMapUrls = useMemo(() => slides.map(slide => slide.depthMapUrl), [slides]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glRef = useRef<WebGL2RenderingContext>(null);
  const programRef = useRef<WebGLProgram>(null);
  const imageTextureRefs = useRef<(WebGLTexture | null)[]>([]);
  const depthTextureRefs = useRef<(WebGLTexture | null)[]>([]);
  const uniformsRef = useRef<Uniforms | null>(null);
  const imageSizeRef = useRef<{ width: number; height: number }>({ width: 1, height: 1 });

  // Active index and blend factor for crossfading
  const selectedIndexRef = useRef(0);
  const targetIndexRef = useRef(0);
  const blendFactor = useMotionValue(0); // 0 = showing selectedIndex, 1 = showing targetIndex
  const animationRef = useRef<ReturnType<typeof animate> | null>(null);

  // Initialize WebGL
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl2");

    if (!gl) {
      logger.error(null, "WebGL2 not supported");
      return;
    }

    glRef.current = gl;

    const vertexShaderSource = `#version 300 es
      in vec2 a_position;
      in vec2 a_texCoord;
      out vec2 v_texCoord;
      
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        v_texCoord = a_texCoord;
      }
    `;

    const fragmentShaderSource = `#version 300 es
      precision mediump float;
      
      uniform sampler2D u_images[${MAX_SLIDES}];
      uniform sampler2D u_depthMaps[${MAX_SLIDES}];
      uniform float u_intensity[${MAX_SLIDES}];
      uniform float u_depthInfluence[${MAX_SLIDES}];
      uniform float u_focusPoint[${MAX_SLIDES}];
      uniform vec2 u_resolution;
      uniform vec2 u_imageSize;
      uniform float u_opacity;
      uniform int u_selectedIndex;
      uniform int u_targetIndex;
      uniform float u_blendFactor;
      
      in vec2 v_texCoord;
      out vec4 fragColor;
      
      vec2 getCoverUV(vec2 uv, vec2 canvasSize, vec2 imageSize) {
        // Calculate scale factors needed to fit each dimension
        float scaleX = canvasSize.x / imageSize.x;
        float scaleY = canvasSize.y / imageSize.y;
        
        // For cover, use the LARGER scale (ensures no empty space)
        float scale = max(scaleX, scaleY);
        
        // Calculate the size the image will be when scaled uniformly
        vec2 scaledImageSize = imageSize * scale;
        
        // Calculate how much we need to crop from each side to fit the canvas
        vec2 overflow = scaledImageSize - canvasSize;
        
        // Convert overflow to UV coordinates (how much to crop from texture)
        vec2 cropUV = overflow / scaledImageSize;
        
        // The UV scale is what remains after cropping
        vec2 uvScale = (scaledImageSize - overflow) / scaledImageSize;
        
        // Center the cropped area
        vec2 offset = cropUV * 0.5;
        vec2 centeredUV = uv * uvScale + offset;
        
        return centeredUV;
      }
      
      vec4 sampleImage(int index, vec2 uv) {
        if (index == 0) return texture(u_images[0], uv);
        else if (index == 1) return texture(u_images[1], uv);
        else if (index == 2) return texture(u_images[2], uv);
        else if (index == 3) return texture(u_images[3], uv);
        else if (index == 4) return texture(u_images[4], uv);
        else if (index == 5) return texture(u_images[5], uv);
        else if (index == 6) return texture(u_images[6], uv);
        else return texture(u_images[7], uv);
      }
      
      float sampleDepthMap(int index, vec2 uv) {
        if (index == 0) return texture(u_depthMaps[0], uv).r;
        else if (index == 1) return texture(u_depthMaps[1], uv).r;
        else if (index == 2) return texture(u_depthMaps[2], uv).r;
        else if (index == 3) return texture(u_depthMaps[3], uv).r;
        else if (index == 4) return texture(u_depthMaps[4], uv).r;
        else if (index == 5) return texture(u_depthMaps[5], uv).r;
        else if (index == 6) return texture(u_depthMaps[6], uv).r;
        else return texture(u_depthMaps[7], uv).r;
      }
      
      void main() {
        vec2 center = vec2(0.5, 0.5);
        vec2 uv = v_texCoord;
        
        // Sample depth maps for both images using cover-transformed coordinates
        vec2 coverUV = getCoverUV(uv, u_resolution, u_imageSize);
        float depthFrom = sampleDepthMap(u_selectedIndex, coverUV);
        float depthTo = sampleDepthMap(u_targetIndex, coverUV);
        
        // Blend depth values
        float depth = mix(depthFrom, depthTo, u_blendFactor);
        
        vec2 delta = uv - center;
        float dist = length(delta);
        
        float depthDiff_active = (depth - u_focusPoint[u_selectedIndex]) * u_depthInfluence[u_selectedIndex];
        float zoom_active = 1.0 + u_intensity[u_selectedIndex] * depthDiff_active;

        float depthDiff_target = (depth - u_focusPoint[u_targetIndex]) * u_depthInfluence[u_targetIndex];
        float zoom_target = 1.0 + u_intensity[u_targetIndex] * depthDiff_target;
        
        vec2 direction = normalize(delta);
        float radiusDist = dist;

        vec2 newUV_active = center + direction * radiusDist * zoom_active;
        vec2 newUV_target = center + direction * radiusDist * zoom_target;
        
        // Apply cover transformation to the final UV coordinates
        vec2 finalUV_active = getCoverUV(newUV_active, u_resolution, u_imageSize);
        vec2 finalUV_target = getCoverUV(newUV_target, u_resolution, u_imageSize);
        
        // Sample both images and blend
        vec4 colorFrom = sampleImage(u_selectedIndex, finalUV_active);
        vec4 colorTo = sampleImage(u_targetIndex, finalUV_target);
        fragColor = mix(colorFrom, colorTo, u_blendFactor) * u_opacity;
      }
    `;

    function createShader(type: number, source: string) {
      if (!gl) return null;
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);

      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        logger.error(null, "Shader compile error", gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    function createProgram(vertexShader: WebGLShader, fragmentShader: WebGLShader) {
      if (!gl) return null;
      const program = gl.createProgram();
      if (!program) return null;
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        logger.error(null, "Program link error", gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
      }
      return program;
    }

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    if (!vertexShader) return;
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!fragmentShader) return;
    const program = createProgram(vertexShader, fragmentShader);

    if (!vertexShader || !fragmentShader || !program) return;

    programRef.current = program;

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const texCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1, 0]), gl.STATIC_DRAW);

    gl.useProgram(program);

    const positionLoc = gl.getAttribLocation(program, "a_position");
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 2, gl.FLOAT, false, 0, 0);

    const texCoordLoc = gl.getAttribLocation(program, "a_texCoord");
    gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
    gl.enableVertexAttribArray(texCoordLoc);
    gl.vertexAttribPointer(texCoordLoc, 2, gl.FLOAT, false, 0, 0);

    uniformsRef.current = {
      intensity: Array.from({ length: MAX_SLIDES }, (_, i) => gl.getUniformLocation(program, `u_intensity[${i}]`)),
      depthInfluence: Array.from({ length: MAX_SLIDES }, (_, i) =>
        gl.getUniformLocation(program, `u_depthInfluence[${i}]`),
      ),
      focusPoint: Array.from({ length: MAX_SLIDES }, (_, i) => gl.getUniformLocation(program, `u_focusPoint[${i}]`)),
      resolution: gl.getUniformLocation(program, "u_resolution"),
      images: gl.getUniformLocation(program, "u_images"),
      depthMaps: gl.getUniformLocation(program, "u_depthMaps"),
      imageSize: gl.getUniformLocation(program, "u_imageSize"),
      opacity: gl.getUniformLocation(program, "u_opacity"),
      selectedIndex: gl.getUniformLocation(program, "u_selectedIndex"),
      targetIndex: gl.getUniformLocation(program, "u_targetIndex"),
      blendFactor: gl.getUniformLocation(program, "u_blendFactor"),
    };

    // Cleanup function for component unmount
    return () => {
      const gl = glRef.current;
      if (!gl) return;

      // Delete textures
      imageTextureRefs.current.forEach(texture => {
        if (texture) {
          gl.deleteTexture(texture);
        }
      });
      imageTextureRefs.current = [];
      depthTextureRefs.current.forEach(texture => {
        if (texture) {
          gl.deleteTexture(texture);
        }
      });
      depthTextureRefs.current = [];

      // Delete buffers
      if (positionBuffer) {
        gl.deleteBuffer(positionBuffer);
      }
      if (texCoordBuffer) {
        gl.deleteBuffer(texCoordBuffer);
      }

      // Delete program (this also detaches shaders)
      if (programRef.current) {
        gl.deleteProgram(programRef.current);
        programRef.current = null;
      }

      // Delete shaders
      if (vertexShader) {
        gl.deleteShader(vertexShader);
      }
      if (fragmentShader) {
        gl.deleteShader(fragmentShader);
      }

      // Clear refs
      glRef.current = null;
      uniformsRef.current = null;
    };
  }, []);

  const render = useCallback(
    function render() {
      const gl = glRef.current;
      const canvas = canvasRef.current;
      if (
        !gl ||
        !canvas ||
        !uniformsRef.current ||
        imageTextureRefs.current.filter(t => t !== null).length !== slides.length ||
        depthTextureRefs.current.filter(t => t !== null).length !== slides.length
      )
        return;
      if (!imageTextureRefs.current[0] || !depthTextureRefs.current[0]) return;

      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.useProgram(programRef.current);

      // Bind all image textures and set uniform array elements
      const program = programRef.current;
      if (!program) return;

      const maxImages = Math.min(imageTextureRefs.current.length, MAX_SLIDES);
      for (let i = 0; i < maxImages; i++) {
        const texture = imageTextureRefs.current[i];
        if (texture) {
          gl.activeTexture(gl.TEXTURE0 + i);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          const location = gl.getUniformLocation(program, `u_images[${i}]`);
          if (location !== null) {
            gl.uniform1i(location, i);
          }
        }
      }

      // Bind all depth map textures and set uniform array elements
      const maxDepthMaps = Math.min(depthTextureRefs.current.length, MAX_SLIDES);
      for (let i = 0; i < maxDepthMaps; i++) {
        const texture = depthTextureRefs.current[i];
        if (texture) {
          const textureUnit = maxImages + i;
          gl.activeTexture(gl.TEXTURE0 + textureUnit);
          gl.bindTexture(gl.TEXTURE_2D, texture);
          const location = gl.getUniformLocation(program, `u_depthMaps[${i}]`);
          if (location !== null) {
            gl.uniform1i(location, textureUnit);
          }
        }
      }

      for (let i = 0; i < MAX_SLIDES; i++) {
        if (!slides[i]) continue;
        const intensity = normalizeParam(slides[i].intensity);
        const depthInfluence = normalizeParam(slides[i].depthInfluence);
        const focusPoint = normalizeParam(slides[i].focusPoint);

        gl.uniform1f(
          uniformsRef.current.intensity[i],
          mix(
            mix(intensity[0][0], intensity[1][0], transitionProgress.get()),
            mix(intensity[0][1], intensity[1][1], transitionProgress.get()),
            idleProgress.get(),
          ),
        );
        gl.uniform1f(
          uniformsRef.current.depthInfluence[i],
          mix(
            mix(depthInfluence[0][0], depthInfluence[1][0], transitionProgress.get()),
            mix(depthInfluence[0][1], depthInfluence[1][1], transitionProgress.get()),
            idleProgress.get(),
          ),
        );
        gl.uniform1f(
          uniformsRef.current.focusPoint[i],
          mix(
            mix(focusPoint[0][0], focusPoint[1][0], transitionProgress.get()),
            mix(focusPoint[0][1], focusPoint[1][1], transitionProgress.get()),
            idleProgress.get(),
          ),
        );
      }

      gl.uniform1f(uniformsRef.current.opacity, typeof opacity === "number" ? opacity : opacity.get());
      gl.uniform1i(uniformsRef.current.selectedIndex, selectedIndexRef.current);
      gl.uniform1i(uniformsRef.current.targetIndex, targetIndexRef.current);
      gl.uniform1f(uniformsRef.current.blendFactor, blendFactor.get());
      gl.uniform2f(uniformsRef.current.resolution, canvas.width, canvas.height);
      gl.uniform2f(uniformsRef.current.imageSize, imageSizeRef.current.width, imageSizeRef.current.height);

      gl.drawArrays(gl.TRIANGLES, 0, 6);
      onRender?.();
    },
    [opacity, onRender, blendFactor, slides, idleProgress, transitionProgress],
  );

  // Debounce the crossfade activation to avoid weirdness when switching mid-transition.
  // The debounce wait time matches the crossfade animation duration.
  const crossFadeDuration = 0.4;
  const [selectedIndexDebounced] = useDebounce(selectedIndex, crossFadeDuration * 1000, { leading: true });
  useEffect(() => {
    if (selectedIndexDebounced === targetIndexRef.current) {
      // Already at target, do nothing
      return;
    }

    selectedIndexRef.current = targetIndexRef.current;
    targetIndexRef.current = selectedIndexDebounced;

    if (animationRef.current) {
      animationRef.current.stop();
    }

    animationRef.current = animate(blendFactor, [0, 1], {
      duration: crossFadeDuration,
      ease: "easeInOut",
    });
  }, [selectedIndexDebounced, blendFactor]);

  // Load textures when URLs change
  useEffect(() => {
    const gl = glRef.current;
    if (!gl || imageUrls.length === 0 || depthMapUrls.length === 0) return;

    // Clean up previous textures
    imageTextureRefs.current.forEach(texture => {
      if (texture) {
        gl.deleteTexture(texture);
      }
    });
    imageTextureRefs.current = [];
    depthTextureRefs.current.forEach(texture => {
      if (texture) {
        gl.deleteTexture(texture);
      }
    });
    depthTextureRefs.current = [];

    const loadedImages: HTMLImageElement[] = [];

    const loadTexture = (
      url: string,
      textureRefs: React.RefObject<(WebGLTexture | null)[]>,
      index: number,
      isMainImage = false,
    ) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        // Check if component is still mounted and GL context is valid
        if (!gl || !glRef.current) return;

        // Store image dimensions for the first main image (used for cover calculation)
        // TODO: handle varying slide dimensions
        if (isMainImage && index === 0) {
          imageSizeRef.current = { width: img.width, height: img.height };
        }

        const texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        // Ensure array is large enough
        while (textureRefs.current.length <= index) {
          textureRefs.current.push(null);
        }
        textureRefs.current[index] = texture;
      };
      img.onerror = () => {
        logger.error(null, "Failed to load image", url);
      };
      img.src = url;
      loadedImages.push(img);

      return img;
    };

    // Load all image textures
    imageUrls.forEach((url, index) => {
      loadTexture(url, imageTextureRefs, index, true);
    });

    // Load all depth map textures
    depthMapUrls.forEach((url, index) => {
      loadTexture(url, depthTextureRefs, index, false);
    });

    return () => {
      // Abort image loading by clearing src
      loadedImages.forEach(img => {
        img.src = "";
      });
    };
  }, [imageUrls.join(","), depthMapUrls.join(","), render]);

  // Initialize canvas size based on rendered size and device pixel ratio
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      // We can get away with a ratio of `1` because we're blurring the canvas.
      // Improves performance versus using `window.devicePixelRatio`.
      const devicePixelRatio = 1;

      const width = Math.floor(displayWidth * devicePixelRatio);
      const height = Math.floor(displayHeight * devicePixelRatio);

      // Only update if size actually changed to avoid unnecessary re-renders
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;

        // Update WebGL viewport
        const gl = glRef.current;
        if (gl) {
          gl.viewport(0, 0, width, height);
        }

        render();
      }
    };

    updateCanvasSize();
    const resizeObserver = new window.ResizeObserver(() => {
      updateCanvasSize();
    });

    resizeObserver.observe(canvas);

    return () => {
      resizeObserver.disconnect();
    };
  }, [render]);

  const inView = useInView(canvasRef);
  const pageInView = usePageInView();

  // Render loop
  useEffect(() => {
    if (!inView || !pageInView) return;
    frame.render(render, true);
    return () => {
      cancelFrame(render);
    };
  }, [render, inView, pageInView]);

  return <canvas ref={canvasRef} className='tw-absolute tw-inset-0 tw-size-full' />;
}

export function DollyZoomAnimated({
  slides,
  selectedIndex = 0,
  isActive = false,
}: {
  slides: DollyZoomSlide[];
  selectedIndex?: number;
  isActive?: boolean;
}) {
  if (slides.length > MAX_SLIDES) {
    throw new Error(`Amount of slides must not be greater than ${MAX_SLIDES}.`);
  }

  const idleProgress = useMotionValue(0);
  const transitionProgress = useMotionValue(0);

  const opacity = useMotionValue(0);
  const startedRef = useRef(false);

  useEffect(() => {
    animate(transitionProgress, isActive ? 1 : 0, { duration: 0.8, ease: [0.7, 0, 0.3, 1] });
  }, [isActive, transitionProgress]);

  const onRender = useCallback(() => {
    if (!startedRef.current) {
      startedRef.current = true;

      animate(opacity, [0, 1], { duration: 1 });

      animate(idleProgress, [0, 1], {
        duration: 30,
        ease: "easeInOut",
        repeat: Infinity,
        repeatType: "reverse",
      });
    }
  }, [opacity, idleProgress]);

  return (
    <DollyZoom
      idleProgress={idleProgress}
      transitionProgress={transitionProgress}
      slides={slides}
      selectedIndex={selectedIndex}
      opacity={opacity}
      onRender={onRender}
    />
  );
}
